'use client';

/**
 * Scheme validators for every `href` or anchor-target derived from
 * attacker-reachable data (shared-project links, imported pattern files,
 * future share-an-inspiration payloads). Fix for SECURITY-TODO.md
 * finding C2 — `javascript:` / `data:text/html` URIs in anchor `href`
 * execute in the current origin when clicked, bypassing
 * `rel="noopener noreferrer"` and `target="_blank"`.
 *
 * Rule of thumb:
 *   - `safeHref(url)` for freeform links (`http(s)` + `mailto` only).
 *   - `safePatternFileHref(url)` for embedded pattern files
 *     (`data:application/pdf` or `blob:` only — never text/html).
 */

/** Allow http(s) and mailto. Everything else, including `javascript:`,
 *  `data:`, `file:`, `vbscript:`, returns undefined. Returning undefined
 *  (vs an empty string) makes React omit the attribute entirely. */
export function safeHref(url: string | undefined | null): string | undefined {
  if (!url) return undefined;
  const trimmed = url.trim();
  if (!trimmed) return undefined;
  // Relative links start with `/` or `#` — allow.
  if (trimmed.startsWith('/') || trimmed.startsWith('#')) return trimmed;
  try {
    const u = new URL(trimmed);
    if (u.protocol === 'http:' || u.protocol === 'https:' || u.protocol === 'mailto:') {
      return trimmed;
    }
    return undefined;
  } catch {
    return undefined;
  }
}

/** For pattern files embedded as data URLs. Only `data:application/pdf`
 *  and `blob:` are allowed. Anything else (especially `data:text/html`)
 *  becomes undefined. */
export function safePatternFileHref(url: string | undefined | null): string | undefined {
  if (!url) return undefined;
  const trimmed = url.trim();
  if (!trimmed) return undefined;
  const lc = trimmed.toLowerCase();
  if (lc.startsWith('data:application/pdf') || lc.startsWith('data:application/octet-stream')) {
    return trimmed;
  }
  if (lc.startsWith('blob:')) return trimmed;
  return undefined;
}
